import { useStorage } from '@quality24/react-hooks';
import moment from 'moment';
import React from 'react';

import merge from 'lodash/merge';
import { DateFilter, FilterDefinition } from '../../@types';
import FilterContext, { IFilterContext } from '../../contexts/filterContext';

const DATE_FILTER_KEY = '_q24_date_filter';
const DATE_FILTER_UPDATE_KEY = '_q24_date_filter_update';

const getDefaultDateFilterByMode = (
  dateFilterMode: Props['dateFilterMode'],
): DateFilter => {
  if (dateFilterMode === 'year') {
    return {
      greaterThanOrEqualTo: moment().startOf('year').toISOString(),
      lessThanOrEqualTo: moment().endOf('year').toISOString(),
    };
  }

  if (dateFilterMode === 'month') {
    return {
      greaterThanOrEqualTo: moment().startOf('month').toISOString(),
      lessThanOrEqualTo: moment().endOf('month').toISOString(),
    };
  }

  return {
    greaterThanOrEqualTo: moment().startOf('month').toISOString(),
    lessThanOrEqualTo: moment().endOf('day').toISOString(),
  };
};

export interface Props {
  children: React.ReactNode;
  initialValues?: IFilterContext['filters'];
  contextFilters?: Record<string, FilterDefinition | Array<FilterDefinition>>;
  ignorePersistence?: boolean;
  dateFilterPartitionId?: string;
  dateFilterMode?: 'date' | 'month' | 'year';
  persist?: boolean;
}

/**
 * Parses the context filters adding the ctx_ prefix to every key
 */
export const parseContextFilters = (
  value: Record<string, FilterDefinition | Array<FilterDefinition>>,
) =>
  Object.entries(value).reduce((acc, [key, val]) => {
    acc[`ctx_${key}`] = val;
    return acc;
  }, {} as Record<string, FilterDefinition | Array<FilterDefinition>>);

const FilterProvider: React.FunctionComponent<Props> = ({
  children,
  initialValues,
  contextFilters,
  ignorePersistence = false,
  dateFilterPartitionId = 'default',
  dateFilterMode = 'date',
  persist,
}) => {
  const { retrieveItem, persistItem } = useStorage();

  const parsedContextFilters = React.useMemo(
    () => (contextFilters ? parseContextFilters(contextFilters) : undefined),
    [contextFilters],
  );

  const initialDateFilter = React.useMemo(() => {
    const lastUpdate = !ignorePersistence
      ? retrieveItem(DATE_FILTER_UPDATE_KEY, null)
      : null;
    const jsonStr = !ignorePersistence
      ? retrieveItem(DATE_FILTER_KEY, null)
      : null;
    const defaultDateFilter = getDefaultDateFilterByMode(dateFilterMode);

    if (
      !lastUpdate ||
      !jsonStr ||
      moment(lastUpdate).isBefore(moment().subtract(1, 'day'))
    ) {
      return defaultDateFilter;
    }

    const jsonDateFilter = JSON.parse(jsonStr);

    /** In case the current date filter partition is empty */
    if (
      !(dateFilterPartitionId in jsonDateFilter) ||
      (dateFilterPartitionId in jsonDateFilter &&
        (!jsonDateFilter[dateFilterPartitionId].lessThanOrEqualTo ||
          !jsonDateFilter[dateFilterPartitionId].greaterThanOrEqualTo))
    ) {
      persistItem(
        DATE_FILTER_KEY,
        JSON.stringify({
          ...jsonDateFilter,
          [dateFilterPartitionId]: defaultDateFilter,
        }),
      );
      return defaultDateFilter;
    }

    return jsonDateFilter[dateFilterPartitionId];
  }, [
    dateFilterMode,
    dateFilterPartitionId,
    ignorePersistence,
    persistItem,
    retrieveItem,
  ]);

  const initialFilters = React.useMemo(() => {
    return merge(
      {
        default: {
          createdAt: initialDateFilter,
          ...parsedContextFilters,
        },
      },
      initialValues,
    );
  }, [initialDateFilter, parsedContextFilters, initialValues]);

  const [filters, setFilters] =
    React.useState<IFilterContext['filters']>(initialFilters);

  /** Sync createdAt filter when initialDateFilter changes  */
  React.useEffect(() => {
    setFilters((curr) => ({
      ...curr,
      default: { ...curr.default, createdAt: initialDateFilter },
    }));
  }, [initialDateFilter]);

  /**
   * Persists the date filter changes
   */
  const updateDateFilterPersistance = React.useCallback(
    (filter: DateFilter) => {
      if (!ignorePersistence) {
        persistItem(DATE_FILTER_UPDATE_KEY, moment().toISOString());

        const storedDateFilter = retrieveItem(DATE_FILTER_KEY, null);

        persistItem(
          DATE_FILTER_KEY,
          JSON.stringify(
            storedDateFilter
              ? {
                  ...JSON.parse(storedDateFilter),
                  [dateFilterPartitionId]: filter,
                }
              : { [dateFilterPartitionId]: filter },
          ),
        );
      }
    },
    [dateFilterPartitionId, ignorePersistence, persistItem, retrieveItem],
  );

  const contextValue = React.useMemo(
    () => ({
      filters,
      setFilters,
      updateDateFilterPersistance,
      dateFilterMode,
      persist,
    }),
    [filters, updateDateFilterPersistance, dateFilterMode, persist],
  );

  return (
    <FilterContext.Provider value={contextValue}>
      {children}
    </FilterContext.Provider>
  );
};

export default FilterProvider;
